import { Injectable } from '@angular/core';
import { IHotelQuoteDetail, IHotelQuoteResourceResponse } from "libraries/service/resources/hotel/hotel-quotation";
import {
    IAccommodation as IBookingAccomodation,
    IBookingPropertyDescription,
    IGetBookingsStayResponse,
    IOriginalCurrency,
    IPrice,
    IPriceDetail,
    IPriceDetails,
    IProperty as IBookingProperty,
    IPropertyRating,
    IRule,
    ISectorStay,
    IService,
    ITraveller as IBookingTraveller,
    IXId,
    XSellLocationEnum
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import {
    IProperty as IQuoteProperty,
    IQuantity,
    IQuotePropertyDescription,
    IQuoteSupplement,
    ITraveller,
    ITraveller as IQuoteTraveller,
    ITravellers
} from "libraries/hotel/models/quote";
import { Keyword, TravellerXType } from "libraries/service/resources/booking/interfaces/get-bookings.enum";
import { SectorXType } from "libraries/service/resources/model/search/air/IAirSector";
import { IAccommodation as IQuoteAccomodation, IMealplan } from "libraries/hotel/models/details";
import { ApiPricingXTarget } from "libraries/service/resources/booking/interfaces/ApiSearchParameters";
import { ISelectedQuote } from "libraries/model/extenal-service/externalService";
import { ICost } from "libraries/service/resources/model/search/IBooking";
import {
    RuleType
} from "libraries/component/booking-information/components/booking-price-card/components/conditions-tab/ICondition";
import { IPenalty } from "libraries/service/resources/hotel/IResourceHotel";
import { IAirTotal } from "libraries/service/resources/model/search/air/IAirTotal";
import { EXRateType, ITotal } from 'libraries/interfaces/ITotal';

@Injectable()
export class QuotationToBookingMappingService {
    public mapBooking(quote: IHotelQuoteResourceResponse, travellerList: IBookingTraveller[] = null, selectedQuote: ISelectedQuote = null):
        Partial<IGetBookingsStayResponse> {

        const property: IBookingProperty[] =
            Object.values(quote.property)
                .map((quoteProperty: IQuoteProperty): IBookingProperty => {
                    const rating: IPropertyRating = quoteProperty.rating ?
                        {
                            value: quoteProperty.rating.standardisationBody,
                            xUnit: 'star'
                        } :
                        undefined;

                    const hostProperty: IQuoteProperty = quote.quote ?
                        quoteProperty.host[quote.quote.host as string].property :
                        Object.values(quoteProperty.host)[0].property;
                    let quoteDescriptions: IQuotePropertyDescription | IQuotePropertyDescription[] =
                        hostProperty && hostProperty.descriptions ? hostProperty.descriptions : [];
                    quoteDescriptions = Array.isArray(quoteDescriptions) ? quoteDescriptions : [quoteDescriptions];
                    const description: IBookingPropertyDescription[] = quoteDescriptions.map((quoteDescription: IQuotePropertyDescription) => {
                        return {
                            ...quoteDescription
                        };
                    });

                    return {
                        description,
                        name: quoteProperty.name,
                        xId: Object.values(quoteProperty.host)[0].syscode,
                        rating
                    };
                });

        let traveller: IBookingTraveller[];

        if (quote.traveller) {
            traveller = Object.values(quote.traveller)
                .map((quoteTraveller: IQuoteTraveller): IBookingTraveller => {
                    return {
                        xId: quoteTraveller.xId,
                        xType: TravellerXType[quoteTraveller.type.toLowerCase()]
                    };
                });
        } else {
            traveller = travellerList;
        }

        const totalCost: ICost = selectedQuote ? selectedQuote.hotelPrices.current : quote.quote.total.cost;
        const totalRetail: ICost = selectedQuote ? selectedQuote.hotelPrices.current : quote.quote.total.retail;

        const currencyToShow: string = Object.keys(totalCost).length === 1 ?
            Object.keys(totalCost)[0] :
            Object.keys(totalCost).filter((currency: string) => currency !== 'EUR')[0];


        const priceTotalTotal: ITotal = {
            value: Number(totalCost[currencyToShow]),
            currency: currencyToShow,
            decimals: 2,
            amount: Number(totalCost[currencyToShow]) / 100,
        };


        const additionalTotal:ITotal =  Object.keys(totalCost).length > 1 && Object.keys(totalCost).includes('EUR') ?
            {
                value: Number(totalCost.EUR),
                currency: 'EUR',
                decimals: 2,
                amount: Number(totalCost.EUR) / 100,
            }: null ;


        // @ts-ignore
        let supplementList: IQuoteSupplement[] = quote.quote && quote.quote.supplement && quote.quote.supplement.total ?
            [quote.quote.supplement] :
            quote.quote && quote.quote.supplement ? quote.quote.supplement : [];

        supplementList = supplementList.flatMap((supplementItem: IQuoteSupplement): IQuoteSupplement[] => {
                const currenciesInCost: string[] = Object.keys(supplementItem.total.cost);
                if(currenciesInCost.length === 1){
                    return [supplementItem];
                }
                return currenciesInCost.map((currencyItem: string): IQuoteSupplement => {
                    return {
                        ...supplementItem,
                        total: {
                            cost: {
                                [currencyItem] : supplementItem.total.cost[currencyItem],
                            }
                        }
                    }
                })
            })

        // @ts-ignore
        const resortFeesList: IQuoteSupplement[] = quote.quote && quote.quote.resortFees && quote.quote.resortFees.total ?
            [quote.quote.resortFees] :
            quote.quote && quote.quote.resortFees ? quote.quote.resortFees : [];

        const merchantSupplementTotalValue: number = (supplementList || [])
            .filter((supplement: IQuoteSupplement) => supplement.xSellLocation === XSellLocationEnum.merchant)
            .reduce((total: number, currentSupplement: IQuoteSupplement) => {
                return total + Number(Object.values(currentSupplement.total.cost)[0]);
            }, 0);

        const localSupplementTotalValue: number = (resortFeesList || [])
            .filter((supplement: IQuoteSupplement) => supplement.xSellLocation === XSellLocationEnum.local)
            .reduce((total: number, currentSupplement: IQuoteSupplement) => {
                return total + Number(currentSupplement.total.cost[priceTotalTotal.currency]);
            }, 0);

        const priceTotalLocal: ITotal = supplementList && supplementList.length ? {
            value: localSupplementTotalValue,
            currency: priceTotalTotal.currency,
            decimals: 2,
            amount: localSupplementTotalValue / 100,
        } : undefined;

        const priceTotalBase: ITotal = supplementList && supplementList.length ?
            {
                value: priceTotalTotal.value - priceTotalLocal.value,
                currency: priceTotalTotal.currency,
                decimals: 2,
                amount: (priceTotalTotal.value - priceTotalLocal.value) / 100,
            }
            : priceTotalTotal;

        const detailPriceBases: IPriceDetail[] = Object.values(quote.quote ? quote.quote.detail : selectedQuote.detail)
            .map((detail: IHotelQuoteDetail): IPriceDetail => {
                const xId: string = detail.room.xId;

                const euroCost: [string, ICost | number] = Object.entries(detail.unit.cost).find(
                    ([key , value]: [string, ICost | number]) : boolean => key === "EUR");
                const originalCurrency: IOriginalCurrency = detail.unit && euroCost && euroCost[1] ?
                    {
                        value: Number(euroCost[1]),
                        currency: 'EUR',
                        amount: Number(euroCost[1]) / 100
                    }: null;

                return {
                    accomodation: [
                        {xId}
                    ],
                    value: detail.unit ? Number(detail.unit.cost[currencyToShow]): null,
                    unitValue: detail.unit ? Number(detail.unit.cost[currencyToShow]): null,
                    amount: detail.unit ? Number(detail.unit.cost[currencyToShow]) / 100: null,
                    unitAmount: detail.unit ? Number(detail.unit.cost[currencyToShow]) / 100: null,
                    originalCurrency,
                    xUnitType: 'adult',
                    currency: currencyToShow,
                    decimals: 2,
                    traveller: detail.rooming ? Object.values(Object.values(detail.rooming)[0].traveller) : undefined,
                };
            });

        const supplement: IPriceDetail[] = supplementList && supplementList.length || resortFeesList && resortFeesList.length ?
            [...supplementList, ...resortFeesList].map((supplementItem: IQuoteSupplement): IPriceDetail => {

                const supplementTravellers: string[] = typeof supplementItem.traveller === 'string' ?
                    [supplementItem.traveller] : supplementItem.traveller;

                const currency: string = supplementItem.total.cost[priceTotalTotal.currency]
                    ? priceTotalTotal.currency : Object.keys(supplementItem.total.cost)[0];

                return {
                    accomodation: [
                        {xId: supplementItem.room}
                    ],
                    xSellLocation: supplementItem.xSellLocation,
                    currency,
                    amount: Number(supplementItem.total.cost[currency]) / 100,
                    unitAmount: Number(supplementItem.total.cost[currency]) / 100,
                    value: Number(supplementItem.total.cost[currency]),
                    unitValue: Number(supplementItem.total.cost[currency]),
                    decimals: 2,
                    traveller: supplementTravellers.map((travellerId: string) => ({xId: travellerId})),
                };
            }) : undefined;

        const detailPrices: IPriceDetails = {
            base: detailPriceBases,
            supplement
        };

        const price: IPrice[] = [
            {
                xId: quote.quote ? quote.quote.xId : null,
                total: {
                    total: priceTotalTotal,
                    base: priceTotalBase,
                },
                additionalTotal,
                detail: detailPrices,
                xTarget: ApiPricingXTarget.total,
                xType: EXRateType.cost,
                keyword: quote.quote && quote.quote.keywords ?
                    quote.quote.keywords.map(
                        (keyword: string) => ({xCode: (keyword.charAt(0).toLowerCase() + keyword.slice(1)) as Keyword})
                    ) : undefined
            }
        ];


        if(totalRetail){
            const additionalTotalRetail:ITotal =  Object.keys(totalRetail).length > 1 && Object.keys(totalRetail).includes('EUR') ?
                {
                    value: Number(totalRetail.EUR),
                    currency: 'EUR',
                    decimals: 2,
                    amount: Number(totalRetail.EUR) / 100,
                }: null ;
            const currencyToShowRetail: string = Object.keys(totalCost).length === 1 ?
                Object.keys(totalRetail)[0] :
                Object.keys(totalRetail).filter((currency: string) => currency !== 'EUR')[0];
            const priceTotalTotalRetail: ITotal = {
                value: Number(totalRetail[currencyToShow]),
                currency: currencyToShowRetail,
                decimals: 2,
                amount: Number(totalRetail[currencyToShowRetail]) / 100,
            };
            const priceTotalBaseRetail: ITotal = supplementList && supplementList.length ?
                {
                    value: priceTotalTotalRetail.value - priceTotalLocal.value,
                    currency: priceTotalTotalRetail.currency,
                    decimals: 2,
                    amount: (priceTotalTotalRetail.value - priceTotalLocal.value) / 100,
                }
                : priceTotalTotalRetail;
            price[1] = {
                xId: quote.quote ? quote.quote.xId : null,
                total: {
                    total: priceTotalTotalRetail,
                    base: priceTotalBaseRetail,
                },
                additionalTotal: additionalTotalRetail,
                xTarget: ApiPricingXTarget.total,
                detail: detailPrices,
                xType: EXRateType.retail
            }
        }

        const total: IAirTotal[] = [
            {
                ...priceTotalTotal, xType: EXRateType.cost
            }
        ];

        const accomodation: IBookingAccomodation[] = Object.keys(quote.rooming || quote.segment[0].rooming)
            .map((roomId: string): IBookingAccomodation => {
                const quoteDetailRoom: IQuoteAccomodation = quote.quote ?
                    quote.quote.detail[`accomodation_${roomId}`].room :
                    selectedQuote.detail[`accomodation_${roomId}`].room;
                const quoteDetailRooming: ITraveller[] = quote.quote ?
                    Object.values(quote.quote.detail[`accomodation_${roomId}`].rooming[roomId].traveller) :
                    Object.values(selectedQuote.detail[`accomodation_${roomId}`].rooming[roomId].traveller);
                return {
                    xId: quoteDetailRoom.xId,
                    syscode: quoteDetailRoom.syscode,
                    code: quoteDetailRoom.code,
                    language: (quoteDetailRoom.name && quoteDetailRoom.name.language) || null,
                    name: (quoteDetailRoom.name && quoteDetailRoom.name.text) || null,
                    quantity: quote.quote ? computeTravellerQuantity(quote.quote.detail[`accomodation_${roomId}`].rooming[roomId]) : selectedQuote.quantity,
                    traveller: quoteDetailRooming
                        .map((quoteTraveller: ITraveller) => {
                            return {
                                xId: quoteTraveller.xId,
                                xType: quoteTraveller.type,
                            };
                        })
                };
            });

        const mealplan: IMealplan = quote.quote ? quote.quote.mealplan : selectedQuote.mealplan;

        const rule: IRule[] = Object.keys(quote.rooming || quote.segment[0].rooming)
            .map((roomId: string): IRule => {
                const quoteDetailRoom: IQuoteAccomodation = quote.quote ? quote.quote.detail[`accomodation_${roomId}`].room : selectedQuote.detail[`accomodation_${roomId}`].room;
                if (!quoteDetailRoom.penalty) {
                    return undefined;
                }

                // @ts-ignore
                const penalties: IPenalty[] = (
                    // @ts-ignore
                    (quoteDetailRoom.penalty.validity || quoteDetailRoom.penalty.keywords) ?
                        [quoteDetailRoom.penalty] : quoteDetailRoom.penalty
                );
                return {
                    xType: RuleType.cancellation,
                    accomodation: {xId: quoteDetailRoom.xId},
                    price: {
                        xId: price[0].xId
                    },
                    penalty: penalties.length ? penalties.map((penalty: IPenalty) => {
                            return {
                                validity: {
                                    cancellation: penalty.validity
                                },
                                price: {
                                    xId: price[0].xId
                                },
                                unitValue: penalty.total && penalty.total.cost ? Object.values(penalty.total.cost)[0].toString() : "0",
                                currency: penalty.total && penalty.total.cost ? Object.keys(penalty.total.cost)[0] : undefined,
                                unitAmount: penalty.total && penalty.total.cost ? Number(Object.values(penalty.total.cost)[0]) / 100 : 0,
                            };
                        }) :
                        undefined,
                };
            });
        const service: IService[] = mealplan ?
            [
                {
                    syscode: mealplan.syscode,
                    name: mealplan.name ? mealplan.name.text : undefined,
                    language: mealplan.name ? mealplan.name.language : undefined,
                    code: mealplan.code,
                    xId: mealplan.xId,
                    mealplan: {
                        xCode: mealplan.xCode,
                    }
                }
            ]
            : undefined;
        return {
            duration: quote.segment[0].duration,
            property,
            traveller,
            begin: quote.segment[0].begin,
            end: quote.segment[0].end,
            price,
            accomodation,
            host: quote.quote ? quote.quote.host as string : selectedQuote.host,
            syscode: quote.quote ? quote.quote.syscode : selectedQuote.propertyName,
            service,
            whenLastToCancelWithoutPenalty: quote.quote ? quote.quote.dateLastCancellationWithoutPenalty : selectedQuote.dateLastCancellationWithoutPenalty,
            total,
            rule,
            sector: [this.mapSector(quote, selectedQuote)]
        };

    }

    public mapSector(quote: IHotelQuoteResourceResponse, selectedQuote: ISelectedQuote = null): ISectorStay {

        const accomodation: IXId[] = Object.keys(quote.segment[0].rooming)
            .map((id: string) => {
                    return {
                        xId: quote.quote ?
                            quote.quote.detail[`accomodation_${id}`].room.xId :
                            selectedQuote.detail[`accomodation_${id}`].room.xId
                    };
                }
            );

        return {
            xType: SectorXType.stay,
            begin: quote.segment[0].begin,
            end: quote.segment[0].end,
            duration: quote.segment[0].duration,
            xId: "S",
            accomodation,
            property: {
                xId: Object.keys(quote.property)[0]
            },
            quantity: {
                room: quote.quantity.rooming,
                adult: quote.quantity.adult,
                infant: quote.quantity.infant,
                child: quote.quantity.child,
                night: quote.quantity.night,
            }
        };
    }
}

function computeTravellerQuantity(quoteDetailRooming: ITravellers): IQuantity {
    return {
        adult: Object.values(quoteDetailRooming.traveller).filter((rooming: ITraveller) => rooming.type.toLowerCase() === TravellerXType.adult).length,
        infant: Object.values(quoteDetailRooming.traveller).filter((rooming: ITraveller) => rooming.type.toLowerCase() === TravellerXType.infant).length,
        child: Object.values(quoteDetailRooming.traveller).filter((rooming: ITraveller) => rooming.type.toLowerCase() === TravellerXType.child).length,
    };
}
